import React, { useState } from 'react';

const BookingModal = ({ isOpen, onClose, selectedStay, showToast }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    guests: '2 Guests',
    checkIn: '',
    checkOut: '',
    message: ''
  });

  const today = new Date().toISOString().split('T')[0];

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validations
    if (formData.name.length < 3) {
      showToast('Please enter a valid name (min 3 characters).');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      showToast('Please enter a valid email address.');
      return;
    }
    const phoneRegex = /^[0-9]{10}$/;
    if (!phoneRegex.test(formData.phone.replace(/[^0-9]/g, ''))) {
      showToast('Please enter a valid 10-digit phone number.');
      return;
    }
    const todayDate = new Date().toISOString().split('T')[0];
    if (formData.checkIn < todayDate) {
      showToast('Arrival date cannot be in the past.');
      return;
    }
    if (formData.checkOut && formData.checkOut <= formData.checkIn) {
      showToast('Check-out date must be after check-in.');
      return;
    }

    setLoading(true);
    
    const data = {
      guest: formData.name,
      email: formData.email,
      stay: selectedStay?.title,
      checkIn: formData.checkIn,
      checkOut: formData.checkOut,
      guests: formData.guests,
      phone: formData.phone,
      details: formData.message
    };

    try {
      const res = await fetch(`https://jaladhara.onrender.com/api/reservations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      if (res.ok) {
        showToast(`Booking request for ${selectedStay?.title} submitted! We will contact you shortly.`);
        onClose();
      }
    } catch (err) {
      console.error('Booking failed:', err);
      showToast('Connection failed. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-emerald-950/80 backdrop-blur-md transition-opacity duration-500"
        onClick={onClose}
      ></div>

      {/* Modal Content */}
      <div className="relative bg-white w-full max-w-xl rounded-2xl overflow-hidden shadow-[0_30px_100px_rgba(0,0,0,0.5)] transform transition-all animate-in fade-in zoom-in duration-300">
        {/* Header */}
        <div className="bg-[#022c22] text-white p-6 relative overflow-hidden">
          {/* Background flourish */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/10 blur-3xl rounded-full -translate-y-1/2 translate-x-1/2"></div>
          
          <button 
            type="button"
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-all duration-300 hover:rotate-90 z-[110]"
            aria-label="Close modal"
          >
            <i className="fas fa-times text-sm"></i>
          </button>
          
          <span className="text-emerald-400 font-bold uppercase tracking-[0.4em] text-[8px] block mb-1 opacity-80">Reservation Portal</span>
          <h2 className="text-2xl font-bold font-serif italic tracking-tight">
            {selectedStay?.title || 'Book Your Stay'}
          </h2>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-[9px] uppercase font-bold text-emerald-800/60 ml-1 tracking-[0.2em]">Guest Name</label>
              <div className="relative">
                <i className="fas fa-user absolute left-4 top-1/2 -translate-y-1/2 text-emerald-600/40 text-xs"></i>
                <input 
                  type="text" 
                  required
                  placeholder="Your Name"
                  className="w-full bg-emerald-50/50 border border-emerald-100 rounded-xl pl-10 pr-4 py-3 focus:outline-none focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-500/50 transition-all placeholder:text-emerald-900/20 text-emerald-950 font-medium text-sm"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                />
              </div>
            </div>
            <div className="space-y-1">
              <label className="text-[9px] uppercase font-bold text-emerald-800/60 ml-1 tracking-[0.2em]">Email Address</label>
              <div className="relative">
                <i className="fas fa-envelope absolute left-4 top-1/2 -translate-y-1/2 text-emerald-600/40 text-xs"></i>
                <input 
                  type="email" 
                  required
                  placeholder="email@example.com"
                  className="w-full bg-emerald-50/50 border border-emerald-100 rounded-xl pl-10 pr-4 py-3 focus:outline-none focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-500/50 transition-all placeholder:text-emerald-900/20 text-emerald-950 font-medium text-sm"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                />
              </div>
            </div>
            <div className="space-y-1">
              <label className="text-[9px] uppercase font-bold text-emerald-800/60 ml-1 tracking-[0.2em]">Phone Number</label>
              <div className="relative">
                <i className="fas fa-phone absolute left-4 top-1/2 -translate-y-1/2 text-emerald-600/40 text-xs"></i>
                <input 
                  type="tel" 
                  required
                  maxLength="10"
                  placeholder="10-digit number"
                  className="w-full bg-emerald-50/50 border border-emerald-100 rounded-2xl pl-12 pr-5 py-4 focus:outline-none focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-500/50 transition-all placeholder:text-emerald-900/20 text-emerald-950 font-medium"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value.replace(/[^0-9]/g, '').slice(0, 10)})}
                />
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-[10px] uppercase font-bold text-emerald-800/60 ml-1 tracking-[0.2em]">Occupancy</label>
              <div className="relative">
                <i className="fas fa-users absolute left-5 top-1/2 -translate-y-1/2 text-emerald-600/40"></i>
                <select 
                  className="w-full bg-emerald-50/50 border border-emerald-100 rounded-2xl pl-12 pr-5 py-4 focus:outline-none focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-500/50 transition-all appearance-none cursor-pointer text-emerald-950 font-medium"
                  value={formData.guests}
                  onChange={(e) => setFormData({...formData, guests: e.target.value})}
                >
                  <option>1 Guest</option>
                  <option>2 Guests</option>
                  <option>3 Guests</option>
                  <option>4+ Guests</option>
                </select>
                <i className="fas fa-chevron-down absolute right-5 top-1/2 -translate-y-1/2 text-emerald-600/30 pointer-events-none text-xs"></i>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div className="space-y-2">
              <label className="text-[10px] uppercase font-bold text-emerald-800/60 ml-1 tracking-[0.2em]">Arrival Date</label>
              <div className="relative">
                <i className="fas fa-calendar-alt absolute left-5 top-1/2 -translate-y-1/2 text-emerald-600/40"></i>
                <input 
                  type="date" 
                  required
                  min={today}
                  className="w-full bg-emerald-50/50 border border-emerald-100 rounded-2xl pl-12 pr-5 py-4 focus:outline-none focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-500/50 transition-all text-emerald-950 font-medium"
                  value={formData.checkIn}
                  onChange={(e) => setFormData({...formData, checkIn: e.target.value})}
                />
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-[10px] uppercase font-bold text-emerald-800/60 ml-1 tracking-[0.2em]">Departure Date</label>
              <div className="relative">
                <i className="fas fa-calendar-check absolute left-5 top-1/2 -translate-y-1/2 text-emerald-600/40"></i>
                <input 
                  type="date" 
                  required
                  min={formData.checkIn || today}
                  className="w-full bg-emerald-50/50 border border-emerald-100 rounded-2xl pl-12 pr-5 py-4 focus:outline-none focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-500/50 transition-all text-emerald-950 font-medium"
                  value={formData.checkOut}
                  onChange={(e) => setFormData({...formData, checkOut: e.target.value})}
                />
              </div>
            </div>
          </div>

          <button 
            type="submit"
            disabled={loading}
            className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-black py-5 rounded-2xl transition-all duration-300 shadow-[0_15px_40px_rgba(5,150,105,0.2)] uppercase text-[11px] tracking-[0.3em] group disabled:opacity-50"
          >
            {loading ? 'SECURING...' : (
              <>
                Secure My Spot <i className="fas fa-arrow-right ml-2 group-hover:translate-x-2 transition-transform"></i>
              </>
            )}
          </button>
          
          <div className="flex items-center justify-center gap-2 pt-2">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
            <p className="text-[10px] text-emerald-900/40 font-bold uppercase tracking-widest">
              Live Reservation Support Active
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BookingModal;
